import {
  PipelineInterface,
  SalesOpportunitiesService as SalesOpportunitiesApiService,
  SalesPipelinesService as SalesPipelinesApiService,
} from '@vendasta/sales-opportunities';
import { Inject, Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { DomainService } from '@vendasta/domain';
import { AUTOMATION_NAMESPACE_INJECTION_TOKEN$ } from '@galaxy/automata/shared';
import { AutomationReplacementChipsDataService, PagedResponse } from '../common/components';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class SalesOpportunitiesService implements AutomationReplacementChipsDataService<PipelineInterface> {
  pipelines$: Observable<PipelineInterface[]>;
  private sscDomain$: Observable<string>;

  anyPipelineOption = {
    name: this.translateService.instant('AUTOMATIONS.EDITOR.TRIGGERS.COMMON.ANY'),
    pipelineId: '*',
  } as PipelineInterface;

  constructor(
    private readonly salesPipelinesService: SalesPipelinesApiService,
    private readonly salesOpportunityService: SalesOpportunitiesApiService,
    protected readonly domainService: DomainService,
    private readonly translateService: TranslateService,
    @Inject(AUTOMATION_NAMESPACE_INJECTION_TOKEN$) public readonly namespace$: Observable<string>,
  ) {
    this.pipelines$ = this.namespace$.pipe(
      switchMap((partnerId) => this.salesPipelinesService.list(partnerId)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this.sscDomain$ = this.namespace$.pipe(
      switchMap((partnerId) => this.domainService.getDomainByIdentifier(`/application/ST/partner/${partnerId}`)),
      map((resp) => {
        return resp.primary.domain;
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  getOpportunityLink(opportunityId: string, accountGroupId: string): Observable<string> {
    return combineLatest([this.salesOpportunityService.get(opportunityId, accountGroupId), this.sscDomain$]).pipe(
      map(([opportunity, sscDomain]) => {
        return `<a href="https://${sscDomain}/info/${accountGroupId}/opportunity/${opportunityId}">${opportunity.name}</a>`;
      }),
    );
  }

  supportsSearching = false;

  convertDataToDisplay(p: PipelineInterface): Observable<string> {
    return of(p.name || '');
  }

  convertDataToFormValue(p: PipelineInterface): string {
    return p.pipelineId || '';
  }

  doLookup(
    searchTerm: string,
    cursor: string,
    pageSize: number,
    extraData?: any,
  ): Observable<PagedResponse<PipelineInterface>> {
    return this.pipelines$.pipe(
      map((pipelines) => {
        pipelines.sort((a, b) => {
          const aName = a?.name?.toLowerCase() || '';
          const bName = b?.name?.toLowerCase() || '';
          return aName < bName ? -1 : 1;
        });
        if (extraData?.prependAnyOption) {
          pipelines.unshift(this.anyPipelineOption);
        }
        return {
          results: pipelines,
          hasMore: false,
          nextCursor: '',
        };
      }),
    );
  }

  getMulti(formValues: string[]): Observable<PipelineInterface[]> {
    return this.pipelines$.pipe(
      map((pipelines) => {
        return pipelines.filter((p) => formValues.includes(p.pipelineId || ''));
      }),
    );
  }
}
